import * as React from 'react';
import { renameFilespace } from '../../services/filespace';
import DescriptionWrapper from '../common/DescriptionWrapper';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { FilespaceNameStep } from './components/FilespaceNameStep';
import { DomainInjectorProperties, injectDomain } from '../hocs/DomainInjector';
import { PageRoutes } from '../root/Routes';
import { RenameCodeVerificationModal } from '../common/modals/RenameCodeVerificationModal';
import { FilespaceAuth, VerificationCode } from 'fs-api-client';
import { createDefaultApi } from '../../utils/createApi';
import { cloudProviders } from 'portal-shared-lib';
import { TOTAL_DOM_FS_ALLOWED_SYMBOLS } from '../../utils/constants';

interface FilespaceInfo {
    id: string;
    name: string;
    email: string;
}

interface FilespaceRenameWizardProperties extends RouteComponentProps, DomainInjectorProperties {
    maxNameLength: number;
}

export interface FilespaceRenameWizardState {
    newFilespaceName: string;
    isRenaming: boolean;
    renameErrorMessage: string;
    waitingVerificationCode: boolean;
    invalidVerificationCode: boolean;
    sendAgainState: SendAgainState;
    hasError: boolean;
    isModalShown: boolean;
    verificationErrorMessage: string | JSX.Element;
    statusCode: number;
    isButtonDisabled: boolean;
}

enum SendAgainState {
    SendAgainReady = 'Send again'
}

class FilespaceRenameWizard extends React.Component<FilespaceRenameWizardProperties, FilespaceRenameWizardState> {
    private fsInfo: FilespaceInfo;
    private maxFilespaceNameLength: number;

    constructor(props: FilespaceRenameWizardProperties) {
        super(props);

        this.onNameChanged = this.onNameChanged.bind(this);
        this.completeStep = this.completeStep.bind(this);

        this.state = {
            newFilespaceName: this.parseQueryFilespaceInfo().name,
            isRenaming: false,
            renameErrorMessage: '',
            waitingVerificationCode: false,
            invalidVerificationCode: false,
            hasError: false,
            sendAgainState: SendAgainState.SendAgainReady,
            isModalShown: false,
            verificationErrorMessage: '',
            statusCode: 0,
            isButtonDisabled: false
        };

        this.onCodeEntered = this.onCodeEntered.bind(this);
        this.onResendCodeClicked = this.onResendCodeClicked.bind(this);
        this.sendVerificationCodeRequest = this.sendVerificationCodeRequest.bind(this);
        this.onModalClose = this.onModalClose.bind(this);
        this.fsInfo = this.parseQueryFilespaceInfo();
        this.maxFilespaceNameLength = this.getMaxFilespaceNameLength();
    }

    render() {

        return (
            <DescriptionWrapper
                title="Rename the Filespace"
                description="Choose a name that identifies this namespace within your domain."
                previousPageTitle={'dashboard'}
                onPreviousPageLinkClick={() => this.props.history.push('/dashboard')}
            >
                <RenameCodeVerificationModal
                    onResendCodeClicked={this.onResendCodeClicked}
                    onCodeEntered={this.onCodeEntered}
                    errorMessage={this.state.verificationErrorMessage}
                    onModalClose={this.onModalClose}
                    userEmail={this.fsInfo.email}
                    isModalShown={this.state.isModalShown}
                    statusCode={this.state.statusCode}
                    isButtonDisabled={this.state.isButtonDisabled}
                />
                <FilespaceNameStep
                    filespaceName={this.state.newFilespaceName}
                    domainName={this.props.domain.name}
                    filespaces={this.props.filespaces.map(x => x.name)}
                    onNameChanged={this.onNameChanged}
                    completeStep={this.sendVerificationCodeRequest}
                    errorMessage={this.state.renameErrorMessage}
                    buttonState={{
                        text: 'Rename',
                        isDisabled: this.state.isButtonDisabled
                    }}
                    maxNameLength={this.maxFilespaceNameLength}
                />
            </DescriptionWrapper>
        );
    }

    private onModalClose(): void {
        this.setState({ renameErrorMessage: '', isModalShown: false });
    }

    private onResendCodeClicked(): void {
        this.setState({ renameErrorMessage: '', verificationErrorMessage: '', });
        this.sendVerificationCodeRequest();
    }

    private onCodeEntered(code: string): void {
        this.completeStep(code);
    }

    private async completeStep(code: string): Promise<void> {
        try {
            this.setState({ isRenaming: true, isButtonDisabled: true });
            const verificationCode = { verificationType: VerificationCode.VerificationTypeEnum.Email, code };

            await renameFilespace(this.fsInfo.id, this.state.newFilespaceName, verificationCode);
            this.props.history.push({ pathname: PageRoutes.Dashboard });
        } catch (e) {
            this.setError(e.response.statusCode);
        } finally {
            this.setState({ isRenaming: false, isButtonDisabled: false });
        }
    }

    private setError(statusCode: number): void {
        let errorMessage;
        if (statusCode === 401) {
            errorMessage = (
                <p className="error">
                    Invalid or expired verification code provided
                </p>
            );
            this.setState(
                {
                    verificationErrorMessage: errorMessage,
                    statusCode: statusCode
                }
            );
        } else {
            errorMessage = (
                <p className="error">The Filespace rename process could not start.
                    The Filespace remains accessible. Please, try again later (up to four hours).
                    If you need assistance, contact us at <a href="mailto: support@lucidlink.com">
                        support@lucidlink.com
                    </a>
                </p>
            );
            this.setState(
                {
                    verificationErrorMessage: errorMessage,
                    statusCode: statusCode
                });
        }
    }

    private async sendVerificationCodeRequest(): Promise<void> {
        try {
            this.setState({ isButtonDisabled: true });
            const api = await createDefaultApi();
            await api.sendFilespaceVerificationCode(
                {
                    verificationType: FilespaceAuth.VerificationTypeEnum.Email,
                    id: this.fsInfo.id,
                    operationType: FilespaceAuth.OperationTypeEnum.Rename
                });
            this.setState({ isModalShown: true });
        } catch (e) {
            const errorMessage = 'We’ve encountered a problem sending you a verification email. ' +
                'This is minor problem and there is no impact on your Filespace.' +
                'Please wait a few minutes and then try again.';
            this.setState({ renameErrorMessage: errorMessage });
        } finally {
            this.setState({ isButtonDisabled: false });
        }
    }

    private parseQueryFilespaceInfo(): FilespaceInfo {
        const queryString = this.props.location.search;
        const withoutQuestionMark = queryString.substr(1);
        const [fsId, fsName, email] = withoutQuestionMark.split('&');
        return {
            id: fsId,
            name: fsName,
            email: email
        };
    }

    private getMaxFilespaceNameLength(): number {
        const filespaceInRename = this.props.filespaces.find((filespace) => {
            return filespace.id === this.fsInfo.id;
        });

        if (filespaceInRename) {
            const provider = cloudProviders[filespaceInRename.storage.provider];

            if (provider) {
                return provider.maxBucketNameLength;
            }
        }

        return TOTAL_DOM_FS_ALLOWED_SYMBOLS;
    }

    private onNameChanged(filespaceName: string): void {
        this.setState({ newFilespaceName: filespaceName });
    }
}

const RenameWizardWithRouter = withRouter(injectDomain(FilespaceRenameWizard) as any);
export { RenameWizardWithRouter as FilespaceRenameWizard };



// WEBPACK FOOTER //
// ./src/webportal/components/wizard/FilespaceRenameWizard.tsx